/**
 * Composable para manejar búsquedas globales
 * Integra con endpoint [GET] /search
 */

import { useApi } from '~/composables/api/useApi'
import { readonly, ref } from 'vue'

// Estado de búsqueda
const query = ref('')
const results = ref([])
const loading = ref(false)
// W9: secuencia de búsquedas (race entre keystrokes del buscador global)
let fetchResultsSeq = 0
const error = ref(null)
const meta = ref(null)

// Tipos de resultado disponibles
const RESULT_TYPES = {
  COMPANY: 'company',
  PRODUCT_ITEM: 'product_item',
  PROMOTION: 'promotion',
  USER: 'user',
  FILE: 'file'
}

// Configuración de tipos para UI
const TYPE_CONFIG = {
  [RESULT_TYPES.COMPANY]: {
    labelKey: 'search.types.company',
    icon: 'mdi:office-building',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-200',
    imageType: 'company'
  },
  [RESULT_TYPES.PRODUCT_ITEM]: {
    labelKey: 'search.types.product',
    icon: 'mdi:package-variant',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    borderColor: 'border-purple-200',
    imageType: 'product'
  },
  [RESULT_TYPES.PROMOTION]: {
    labelKey: 'search.types.promotion',
    icon: 'material-symbols:apartment',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-200',
    imageType: 'promotion'
  },
  [RESULT_TYPES.USER]: {
    labelKey: 'search.types.user',
    icon: 'mdi:account',
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    borderColor: 'border-orange-200',
    imageType: 'user'
  },
  [RESULT_TYPES.FILE]: {
    labelKey: 'search.types.file',
    icon: 'mdi:file-document',
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    borderColor: 'border-slate-200',
    imageType: 'file'
  }
}

/**
 * Genera la ruta de navegación según el tipo de resultado
 * @param {string} type - Tipo de entidad
 * @param {string} id - ID de la entidad
 * @param {Object} attributes - Atributos de la entidad
 * @returns {string} - Ruta de navegación
 */
const getResultRoute = (type, id, attributes = {}) => {
  switch (type) {
    case RESULT_TYPES.COMPANY:
      return `/companies/${id}`

    case RESULT_TYPES.PRODUCT_ITEM:
      return `/product-items/${id}`

    case RESULT_TYPES.PROMOTION:
      return `/promotions/${id}`

    case RESULT_TYPES.USER:
      return `/users/${id}`

    case RESULT_TYPES.FILE: {
      // Los ficheros van a la sección de ficheros de su promoción con query param
      const promotionId = attributes?.promotionId
      if (promotionId) {
        return `/promotions/${promotionId}/files?fileId=${id}`
      }
      // Si no tiene promoción, no debería pasar según permisos, pero por si acaso
      return '#'
    }

    default:
      return '#'
  }
}

/**
 * Extrae el título del resultado según su tipo
 * @param {string} type - Tipo de entidad
 * @param {Object} attributes - Atributos de la entidad
 * @returns {string} - Título a mostrar
 */
const getResultTitle = (type, attributes = {}) => {
  switch (type) {
    case RESULT_TYPES.COMPANY:
      return attributes.commercialName || attributes.fiscalName || 'Sin nombre'

    case RESULT_TYPES.PRODUCT_ITEM:
      return attributes.title || 'Sin título'

    case RESULT_TYPES.PROMOTION:
      return attributes.name || 'Sin nombre'

    case RESULT_TYPES.USER:
      return attributes.displayName || `${attributes.firstname || ''} ${attributes.lastname || ''}`.trim() || attributes.email || 'Sin nombre'

    case RESULT_TYPES.FILE:
      return attributes.name || 'Sin nombre'

    default:
      return 'Sin título'
  }
}

/**
 * Extrae el subtítulo del resultado según su tipo
 * @param {string} type - Tipo de entidad
 * @param {Object} attributes - Atributos de la entidad
 * @returns {string} - Subtítulo a mostrar
 */
const getResultSubtitle = (type, attributes = {}) => {
  switch (type) {
    case RESULT_TYPES.COMPANY:
      return attributes.description || attributes.address?.city || ''

    case RESULT_TYPES.PRODUCT_ITEM:
      return attributes.description || attributes.brand || ''

    case RESULT_TYPES.PROMOTION:
      return attributes.description || attributes.address?.city || ''

    case RESULT_TYPES.USER:
      return attributes.email || ''

    case RESULT_TYPES.FILE:
      return attributes.category || ''

    default:
      return ''
  }
}

/**
 * Extrae la imagen del resultado según su tipo
 * @param {string} type - Tipo de entidad
 * @param {Object} attributes - Atributos de la entidad
 * @returns {string|null} - Path de la imagen o null
 */
const getResultImage = (type, attributes = {}) => {
  switch (type) {
    case RESULT_TYPES.COMPANY:
      return attributes.logo || null

    case RESULT_TYPES.PRODUCT_ITEM:
      return attributes.image || null

    case RESULT_TYPES.PROMOTION:
      return attributes.image || null

    case RESULT_TYPES.USER:
      return attributes.avatar || null

    case RESULT_TYPES.FILE:
      // Los ficheros no tienen imagen, devolvemos null para mostrar icono
      return null

    default:
      return null
  }
}

/**
 * Mapea un resultado del API al formato de UI
 * @param {Object} result - Resultado del API
 * @returns {Object} - Resultado formateado para UI
 */
const mapResultToUI = (result) => {
  const { type, id, attributes } = result
  const config = TYPE_CONFIG[type] || TYPE_CONFIG[RESULT_TYPES.FILE]

  return {
    id,
    type,
    title: getResultTitle(type, attributes),
    subtitle: getResultSubtitle(type, attributes),
    image: getResultImage(type, attributes),
    imageType: config.imageType,
    url: getResultRoute(type, id, attributes),
    raw: result, // Datos crudos por si se necesitan
    meta: {
      typeLabel: config.labelKey,
      icon: config.icon,
      color: config.color,
      bgColor: config.bgColor,
      borderColor: config.borderColor
    }
  }
}

/**
 * Realiza la búsqueda contra el API
 * @param {string} searchQuery - Query de búsqueda
 * @param {Array<string>|null} types - Tipos a buscar (null = todos)
 * @param {Object} options - Opciones adicionales
 */
const fetchResults = async (searchQuery, types = null, options = {}) => {
  if (!searchQuery || searchQuery.trim().length < 2) {
    fetchResultsSeq++
    results.value = []
    meta.value = null
    return
  }

  // W9: secuencia — una búsqueda anterior en vuelo no puede pisar la última
  const seq = ++fetchResultsSeq
  loading.value = true
  error.value = null

  const { page = 1, size = 10 } = options

  try {
    const { makeRequest } = useApi()

    // Construir query params
    const params = new URLSearchParams()
    params.set('q', searchQuery.trim())
    params.set('page', page.toString())
    params.set('size', size.toString())

    if (types && types.length > 0) {
      params.set('types', types.join(','))
    }

    const url = `/search?${params.toString()}`
    const response = await makeRequest({
      url: url,
      method: 'GET'
    })

    // Guardar meta para posible paginación futura
    if (seq !== fetchResultsSeq) return
    meta.value = response.meta || null

    // Mapear resultados al formato de UI
    if (response.data && Array.isArray(response.data)) {
      results.value = response.data.map(mapResultToUI)
    } else {
      results.value = []
    }
  } catch (err) {
    if (seq !== fetchResultsSeq) return
    error.value = err.message || 'Error al realizar la búsqueda'
    console.error('Search error:', err)
    results.value = []
  } finally {
    if (seq === fetchResultsSeq) loading.value = false
  }
}

/**
 * Composable principal
 */
export const useSearch = () => {
  /**
   * Realizar búsqueda
   * @param {string} searchQuery - Query de búsqueda
   * @param {Object} options - Opciones adicionales (page, size, types)
   */
  const search = async (searchQuery, options = {}) => {
    query.value = searchQuery
    await fetchResults(searchQuery, options.types, options)
  }

  /**
   * Realizar búsqueda filtrada por tipos
   * @param {string} searchQuery - Query de búsqueda
   * @param {Array<string>} types - Array de tipos permitidos
   * @param {Object} options - Opciones adicionales (page, size)
   */
  const searchWithTypes = async (searchQuery, types, options = {}) => {
    query.value = searchQuery
    await fetchResults(searchQuery, types, options)
  }

  /**
   * Limpiar búsqueda
   */
  const clear = () => {
    query.value = ''
    results.value = []
    error.value = null
    meta.value = null
  }

  /**
   * Obtener resultados por tipo
   * @param {string} type - Tipo de resultado a filtrar
   */
  const getResultsByType = (type) => {
    return results.value.filter(r => r.type === type)
  }

  /**
   * Obtener configuración de un tipo
   * @param {string} type - Tipo de entidad
   */
  const getTypeConfig = (type) => {
    return TYPE_CONFIG[type] || null
  }

  /**
   * Obtener todos los tipos disponibles
   */
  const getAvailableTypes = () => {
    return Object.values(RESULT_TYPES)
  }

  return {
    // Estado (readonly)
    query: readonly(query),
    results: readonly(results),
    loading: readonly(loading),
    error: readonly(error),
    meta: readonly(meta),

    // Métodos
    search,
    searchWithTypes,
    clear,
    getResultsByType,
    getTypeConfig,
    getAvailableTypes,

    // Helpers centralizados
    getResultRoute,
    getResultTitle,
    getResultSubtitle,
    getResultImage,

    // Constantes
    RESULT_TYPES,
    TYPE_CONFIG
  }
}
